import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ContentChildren,
    ElementRef,
    EventEmitter,
    Inject,
    NgZone,
    OnDestroy,
    OnInit,
    QueryList,
    TemplateRef,
    ViewChild,
    ViewChildren
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { BehaviorSubject, combineLatest, forkJoin, Observable, of, Subject } from 'rxjs';
import {
    concatMap,
    debounceTime,
    distinctUntilChanged,
    finalize,
    map,
    mergeMap,
    switchMap,
    take,
    tap
} from 'rxjs/operators';
import {
    DatasetField,
    DatasetInput,
    DatasetInputChangeEventArgs,
    DatasetInputGroup,
    DatasetTab,
    DefaultDatasetFieldIcon,
    DefaultGlobalFilter,
    DefaultGlobalFilterTypeOptions,
    GlobalFilter,
    GlobalFilterChangedArgs,
    GlobalFilterType,
    JoinDialogDataset,
    JoinDialogDatasetField,
    JoinDialogModel,
    JoinTypeOption,
    JoinTypeOptions,
    Product,
    ProductDataset,
    ProductDatasetGroup
} from 'src/app/pivot-studio/models/dataset-editor.models';
import {
    AddOrUpdateUserDatasetDto,
    AdvancedDatasetDropdownInputDto,
    AdvancedDatasetDto,
    AdvancedDatasetFieldDto,
    AdvancedDatasetInputDto,
    AdvancedDatasetInputKind,
    AdvancedDatasetInputType,
    AdvancedDatasetRelativeDatesInputValueDto,
    AdvancedDatasetRelativeDatesOperator,
    AppSettingsDto,
    CustomDatasetService,
    DatasetDefaultJoinFieldsLookupDto,
    DatasetJoinType,
    DatasetTemplateReferencePairDto,
    ErrorDto,
    JointDatasetFieldDto,
    JointUserDatasetContentDto,
    ListUserDatasetDto,
    SchemaFieldTargetDto,
    SingleUserDatasetContentDto,
    StoredUserDatasetValidationStateDto,
    UserDatasetContentDto,
    UserDatasetContentType,
    UserDatasetDto,
    UserDatasetService,
    VisualService
} from 'src/app/services/api.services';
import { ClonerService } from 'src/app/shared/clone/cloner-service';
import { GenericAlertDialog } from 'src/app/shared/dialogs';
import { DraggableDeltaEvent } from 'src/app/shared/directives/draggable.directive';
import { AppUser } from 'src/app/shared/models/app-user';
import {
    AppError,
    catchAppError,
    combineForLoading,
    combineLogicalOr,
    createSearchPattern,
    distinctBy,
    filterToDistinct,
    getIconForKnownProduct,
    getTitleForKnownProduct,
    group,
    Grouping,
    isNullOrUndefined,
    isNullOrWhitespace,
    productOrderSort
} from 'src/app/shared/utils';
import { AppSelectors } from 'src/app/state/app';
import { maximumUnitsFromUnitType, unitsAsMonths } from '../../dataset-editor/input.component';
import { VisualEditorChangeDatasetDialog } from './change-dataset.dialog';
import { DataSchemaItem } from 'src/app/pbir/dataModel';
import { logger } from '../../../../pbir/utils/logger';

const LOGGER = logger('DatasetEditorDialogComponent');

@Component({
  selector: 'app-dataset-editor-dialog',
  templateUrl: './dataset-editor-dialog.component.html',
  styleUrls: ['./dataset-editor-dialog.component.scss'],
  standalone: false
})
export class DatasetEditorDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  locale = 'en-US';

  constructor(
    private ref: ChangeDetectorRef,
    private dialog: MatDialog,
    private clonerService: ClonerService,
    private store: Store,
    private customDatasetService: CustomDatasetService,
    private userDatasetService: UserDatasetService,
    private visualService: VisualService,
    private dialogRef: MatDialogRef<DatasetEditorDialogComponent>,
    private ngZone: NgZone,
    @Inject(MAT_DIALOG_DATA) private data: DatasetEditorDialogData) {
    this.locale = data.appSettings.locale;
  }

  loadingUserDataset$ = new BehaviorSubject<boolean>(false);
  userDatasetId: number;
  userDataset: UserDatasetDto;
  userDatasetContents: FlatDatasetListItem[] = [];

  loading$: Observable<boolean>;
  saving$: Observable<boolean>;
  busy$: Observable<boolean>;
  validateUIEmitter = new EventEmitter<void>();
  updateFieldsEmitter = new EventEmitter<void>();

  propertiesDirty = false;
  pristine = true;

  // when adding a join for the first time, toggle in the joinable datasets filter
  firstTimeAddingJoin = true;

  @ContentChildren('.input') inputElementsQuery: QueryList<HTMLElement>;

  _destroyed = false;

  user$: Observable<AppUser>;
  adminUser = false;

  ngOnInit() {
    this.loading$ = combineForLoading(
      this.loadingProducts$,
      this.loadingUserDataset$,
      this.loadingDefaultJoinFields$
    );

    this.saving$ = combineLogicalOr(
      this.savingUserDataset$
    );

    this.busy$ = combineLogicalOr(
      this.loading$,
      this.saving$
    );

    this.user$ = this.store.select(AppSelectors.getUser);

    this.initDataset();

    this.loadProductDatasetTemplates().subscribe(() => {
      this.loadDefaultJoinFields();
      this.loadUserDataset();
    });
  }

  ngOnDestroy() {
    this._destroyed = true;
  }

  ngAfterViewInit() {
    this.jointDatasetsContainer.changes.subscribe(() => {
      this.updateJointDatasetsContainerOffset();
    });
  }

  close() {
    this.dialogRef.close();
  }

  save() {
    this.pristine = false;

    const invalidRequiredInputs = this.getInvalidRequiredInputs();
    const incompleteTabs = this.datasetTabs.filter(t => !t.datasetId);

    const errors: string[] = [];

    if (incompleteTabs.length) {
      errors.push(
        `<p>There are <strong>${incompleteTabs.length}</strong> incomplete datasets.</p>`);
    }

    for (let i = 0; i < this.datasetTabs.length - 1; ++i) {
      const tab = this.datasetTabs[i];
      if (!tab.datasetId || !this.datasetTabs[i + 1]?.datasetId)
        continue;

      const potentiallyValidFields = tab.joinFields?.filter(f => f.leftFieldId || f.rightFieldId) ?? [];

      if (!potentiallyValidFields.length) {
        tab.invalid = true;

        errors.push(
          `<p>The join between <strong>Dataset #${i + 1}</strong> and <strong>Dataset #${i + 2}</strong> `
          + `does not have any fields.</p>`);
      }
      else if (potentiallyValidFields.find(f => (f.leftFieldId && !f.rightFieldId) || (f.rightFieldId && !f.leftFieldId))) {
        tab.invalid = true;

        errors.push(
          `<p>The join between <strong>Dataset #${i + 1}</strong> and <strong>Dataset #${i + 2}</strong> `
          + `has one or more incomplete fields.</p>`);
      }
    }
    const invalidTabs: DatasetTab[] = [];
    if (invalidRequiredInputs.length > 0) {
      // Mark related tabs as invalid
      for (const datasetId of filterToDistinct(invalidRequiredInputs.map(i => i.datasetId))) {
        const tab = this.datasetTabs.find(t => t.datasetId === datasetId);
        if (tab) {
          tab.invalid = true;
          invalidTabs.push(tab);
        }
      }
      
      const emptyInputs = invalidRequiredInputs.filter(a => a.error == null);

      if (emptyInputs.length > 0) {
        errors.push(
          `<p>There are <strong>${emptyInputs.length}</strong> inputs which require a value.</p>`);
      }

      const inputErrors = invalidRequiredInputs
        .filter(a => !!a.error)
        .map(i => `<p>${i.error}</p>`);

      errors.push(...inputErrors);
    }

    const selectedFields: DatasetField[] = [];

    for (const tab of this.datasetTabs) {
      const state = this.datasetStateMap.get(tab.datasetId);
      if(state)
        selectedFields.push(...state.fields.filter(f => (!f.exclude && !f.field.hidden)));
    }

    if (selectedFields.length <= 0)
      errors.push(
        `<p>Please select at least <strong>One</strong> Dataset Field</p>`);

    if (errors.length > 0) {
      return GenericAlertDialog.warn(this.dialog, errors.join('')).subscribe(() => {
        if (invalidTabs.length > 0) {
          const currentTab = this.datasetTabs[this.selectedDatasetTabIndex];

          let viewTabInputs;
          if (invalidTabs.includes(currentTab)) {
            viewTabInputs = invalidRequiredInputs.filter(i => i.datasetId === currentTab.datasetId);

            this.markInvalidRequiredInputs(viewTabInputs, true);
          }
          else {
            viewTabInputs = invalidRequiredInputs.filter(i => i.datasetId === invalidTabs[0].datasetId);

            this.setSelectedDatasetTab(this.datasetTabs.indexOf(invalidTabs[0]));

            //wait for the DOM update since this uses http refs
            setTimeout(() => {
              if (this._destroyed)
                return;

              this.markInvalidRequiredInputs(viewTabInputs, true);
            });
          }
        }
      });
    }

    const contentDto = this.createUserDatasetContentForSave();

    const changes = this.classifyContentChanges(contentDto);

    LOGGER.debug(`Dataset changes classified as: `, {
      ...changes,
      kind: DatasetChangesKind[changes.kind]
    });

    if (changes.kind == DatasetChangesKind.None)
      this.dialogRef.close();

    if (!!this.data.referencedFields?.length && changes.kind > DatasetChangesKind.DatasetChangeNoFieldsBroken) {
      this.dialog.open(VisualEditorChangeDatasetDialog, {
        width: '600px', panelClass:'dialog-no-padding', data: {
          kind: changes.kind,
        }
      }).afterClosed()
        .subscribe(result => {
          if (result) {
              this.saveUserDataset(contentDto, changes.kind);
          }
        });
    } else {
      this.saveUserDataset(contentDto, changes.kind);
    }
  }

  private markInvalidRequiredInputs(inputs: { input: DatasetInput, datasetId: number; }[], scrollIntoView = false) {
    let firstInputIndex: number;
    let firstHeight: number;

    for (const input of inputs) {
      if (input.input.template.type == AdvancedDatasetInputType.Dropdown) {
        input.input.value = null;
      }

      const inputId = input.input.id;
      const inputClass = `dataset-editor-input-${inputId}`;

      const elements = document.getElementsByClassName(inputClass);
      for (const element of Array.from(elements)) {
        // Mark as touched
        element.classList.remove('ng-pristine');
        element.classList.add('ng-dirty');

        if (input.input.template.type == AdvancedDatasetInputType.Dropdown) {
          element.classList.add('ng-invalid');
        }

        const box = element.getBoundingClientRect();

        if (!firstHeight || box.y < firstHeight) {
          firstHeight = box.y;
          firstInputIndex = inputs.indexOf(input);
        }
      }
      this.validateUIEmitter.emit();
    }

    if (scrollIntoView) {
      const foucuseElement = inputs[firstInputIndex];

      //multi input groups require special handling
      const multi = foucuseElement.input.inputs ? '-multiwrapper' : '';

      let foucuseElementRef = document.getElementsByClassName(`dataset-editor-input-${foucuseElement.input.id}` + multi).item(0);

      if (!multi) {
        foucuseElementRef = foucuseElementRef.closest("app-dataset-editor__input");
      }

      foucuseElementRef.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  private classifyContentChanges(contentDto: UserDatasetContentDto): DatasetChangesClassification | null {
    if (!this.data.userDatasetId) {
      return { kind: DatasetChangesKind.DatasetChange };
    }

    const previousContents = this.getFlatDatasetListFromContent(this.userDataset.content);
    const currentContents = this.getFlatDatasetListFromContent(contentDto);

    const contentLength = Math.max(previousContents.length, currentContents.length);

    const contentList = [...Array(contentLength)].map((_, i) => [previousContents[i] ?? null, currentContents[i] ?? null]);

    const classification: DatasetChangesClassification = {
      kind: getDatasetChangeBaseKind(this.data.referencedFields),
      datasets: []
    };

    if (classification.kind == DatasetChangesKind.DatasetChange)
      return { kind: DatasetChangesKind.DatasetChange };

    if (this.propertiesDirty)
      classification.kind = Math.max(classification.kind, DatasetChangesKind.Refresh)

    for (let i = 0; i < contentList.length; ++i) {
      const [previous, current] = contentList[i];

      const datasetChanges: any = {
        inputs: [],
        fields: [],
        joinFields: false,
        joinTypes: false
      };


      if (isNullOrUndefined(previous) || isNullOrUndefined(current)) {
        classification.datasets.push(null);
        // if it is a newly added dataset or a removed dataset we cant do this operation to preserve order we push null
        continue;
      }

      if (!joinFieldsEqual(previous.fields, current.fields)) {
        datasetChanges.joinFields = true;
      }

      if (previous.joinType != current.joinType) {
        datasetChanges.joinTypes = true;
      }

      datasetChanges.inputs = getInputsChangeSet(previous.dataset.inputs, current.dataset.inputs);
      datasetChanges.fields = getFieldsChangeSet(previous.dataset.fields, current.dataset.fields);

      classification.datasets.push(datasetChanges);
    }

    for (const changes of classification.datasets) {
      if (!changes)
        continue;
      // Inputs changed - defintion may have changed
      // Fields added/removed - definition may have changed
      if (changes.inputs.any || changes.fields.any || changes.joinFields || changes.joinTypes) {
        classification.kind = Math.max(classification.kind, DatasetChangesKind.Refresh);
      }
    }

    return classification;


    function getFieldsChangeSet(l: AdvancedDatasetFieldDto[], r: AdvancedDatasetFieldDto[]): DatasetElementChangeSet<AdvancedDatasetFieldDto> {
      const changeSet = new DatasetElementChangeSet<AdvancedDatasetFieldDto>(
        x => x.id,
        x => x.hidden ? true : !!x.exclude,
        (a, b) => fieldSame(a, b)
      );

      changeSet.calculate(l, r);

      return changeSet;

      function fieldSame(a: AdvancedDatasetFieldDto, b: AdvancedDatasetFieldDto) {
        if (a === b)
          return true;

        // Hidden fields aren't referencable in visuals, hence we don't care if they disappear from the dataset
        const aHidden = a.hidden || a.exclude;
        const bHidden = b.hidden || b.exclude;

        if (!(a.order == b.order && aHidden == bHidden && a.name == b.name && a.title == b.title))
          return false;

        return true;
      }
    }

    function getInputsChangeSet(l: AdvancedDatasetInputDto[], r: AdvancedDatasetInputDto[]): DatasetElementChangeSet<AdvancedDatasetInputDto> {
      const changeSet = new DatasetElementChangeSet<AdvancedDatasetInputDto>(
        x => x.id,
        x => !!x.details.hidden,
        (a, b) => inputSame(a, b)
      );

      changeSet.calculate(l, r);

      return changeSet;

      function inputSame(a: AdvancedDatasetInputDto, b: AdvancedDatasetInputDto) {
        if (a === b)
          return true;

        if (a.type === b.type) {
          // INFO: Inputs are not compared value wise at the moment
          // We depent on dirty checks in the editor
          // Still, if type has changed, then we are certain we need refresh, cause the param form would not match
          return true;
        }

        return false;
      }
    }

    function joinFieldsEqual(l: JointDatasetFieldDto[], r: JointDatasetFieldDto[]) {
      if (l.length !== r.length)
        return false;

      if (l.find((f, i) => !fieldEqual(f, r[i]))) {
        return false;
      }

      return true;

      function fieldEqual(l: JointDatasetFieldDto, r: JointDatasetFieldDto) {
        return l.leftFieldId == r.leftFieldId && l.rightFieldId == r.rightFieldId;
      }
    }

    function getDatasetChangeBaseKind(referencedFields: DatasetReferencedField[]): DatasetChangesKind {
      const wasJointDataset = previousContents.length > 1;
      const newIsJointDataset = currentContents.length > 1;


      // if we are up or down grading from a join we indexing either didnt exist or is being created as a result
      // if we are changing a joint dataset without downgrading it to single we need to respect indexing
      if (wasJointDataset && !newIsJointDataset) {
        return changeIndexUnsafe();
      } else {
        return changeIndexSafe();
      }

      function getDatasetInUse(datasetIndex: number, referencedFields: DatasetReferencedField[]): boolean {
        return referencedFields.some(f => {
          return f.index == datasetIndex;
        }
        );
      }

      function changeIndexSafe() {
        let changedButNotBroken: boolean;

        for (let i = 0; i < contentList.length; ++i) {
          const [previous, current] = contentList[i];
          const isNewDataset = isNullOrUndefined(previous);

          const datasetChanged = !(previous?.productName == current?.productName && previous?.dataset.id == current?.dataset.id);

          if (isNewDataset || datasetChanged) {
            changedButNotBroken = true;
          }

          if (isNewDataset || !datasetChanged) {
            continue;
          }

          if (getDatasetInUse(i, referencedFields))
            return DatasetChangesKind.DatasetChange;
        }

        if (changedButNotBroken)
          return DatasetChangesKind.DatasetChangeNoFieldsBroken;

        return DatasetChangesKind.None;
      }

      function changeIndexUnsafe() {
        let changedButNotBroken;
        for (let i = 0; i < previousContents.length; ++i) {
          const previous = previousContents[i];
          if (isNullOrUndefined(previous))
            continue;

          const datasetRemoved = !currentContents.some(c =>
            c.productName == previous.productName && c.dataset.id == previous.dataset.id
          );

          if (datasetRemoved) {
            changedButNotBroken = true;
            if (getDatasetInUse(i, referencedFields))
              return DatasetChangesKind.DatasetChange;
          }
        }

        return changedButNotBroken ? DatasetChangesKind.DatasetChangeNoFieldsBroken : DatasetChangesKind.None;
      }
    }

  }
  //#region User Dataset
  savingUserDataset$ = new BehaviorSubject<boolean>(false);

  private loadUserDataset() {
    if (this.data.userDatasetId) {
      this.userDatasetId = this.data.userDatasetId;

      this.loadingUserDataset$.next(true);

      this.userDatasetService.getUserDataset(this.data.userDatasetId, true, false).pipe(
        tap(ud => {
          this.userDataset = ud;
          this.applyLoadedUserDataset();
        }),
        catchAppError(err => of(err))
      ).subscribe(() => {
        this.loadingUserDataset$.next(false);
      });
    }
    else {
      this.firstTimeAddingJoin = true;

      this.userDatasetId = 0;

      this.loadingUserDataset$.next(false);
    }
  }

  private saveUserDataset(
    contentDto: UserDatasetContentDto,
    changesKind: DatasetChangesKind) {
    this.savingUserDataset$.next(true);

    const addOrUpdateDto: AddOrUpdateUserDatasetDto = {
      id: 0,
      name: this.getNameForSave(contentDto),
      content: contentDto
    };

    const reset = changesKind >= DatasetChangesKind.Reset;
    const refresh = changesKind >= DatasetChangesKind.Refresh;
    const reload = changesKind >= DatasetChangesKind.DatasetChangeNoFieldsBroken;

    if (!reset && this.userDatasetId)
      addOrUpdateDto.initialModelFromUserDatasetId = this.userDatasetId;

    this.userDatasetService.addOrUpdateUserDataset(addOrUpdateDto).pipe(
      switchMap((userDataset) => {
        return forkJoin(
          of(userDataset),
          this.userDatasetService.validate(userDataset.id),
        )
      }),
      tap(([userDataset, validationState]) => {
        this.pristine = true;
        this.propertiesDirty = false;
        
        this.dialogRef.close(<DatasetEditorDialogResult>{
          userDataset,
          validationState,
          reset,
          refresh,
          reload
        });
      }),
      catchAppError(err => of(err))
    ).subscribe(() => {
      this.dialogRef.disableClose = false;
      this.savingUserDataset$.next(false);
    });
  }

  private getNameForSave(contentDto: UserDatasetContentDto) {
    if (isNullOrUndefined(contentDto.type) || contentDto.type == UserDatasetContentType.Single) {
      return (contentDto as SingleUserDatasetContentDto).dataset?.details?.name ?? '';
    }

    return 'Joined Dataset';
  }

  private applyLoadedUserDataset() {
    const datasets = this.getFlatDatasetListFromContent(this.userDataset.content);

    this.userDatasetContents = datasets;

    this.firstTimeAddingJoin = datasets.length < 2;

    for (let i = 0; i < datasets.length; ++i) {
      const dataset = datasets[i];

      this.applyLoadedDataset(dataset.productName, dataset.dataset, i);
    }

    for (let i = 0; i < datasets.length; ++i) {
      const dataset = datasets[i];

      this.applyLoadedDatasetJoins(i, dataset.joinType, dataset.fields);
    }

    this.setSelectedDatasetTab(0);

    this.propertiesDirty = false;
    this.pristine = true;

    this.applyLoadedDatasetFields();
  }

  private applyLoadedDataset(
    productName: string,
    source: AdvancedDatasetDto,
    tabIndex: number) {
    // Find the dataset template this user dataset is based on (by product/dataset template name)
    const productDataset =
      this.products$.value.find(p => p.name == productName)
        ?.datasetGroups.reduce((a, g) => [...a, ...g.datasets], <ProductDataset[]>[])
        ?.find(d => d.template.id == source.id);

    const dataset = this.datasetsById.get(productDataset.id);

    if (dataset.template.sourceType != source.sourceType) {
      LOGGER.error([
        `The user dataset template value sourceType '${source.sourceType}'`,
        `does not match the product template value '${dataset.template.sourceType}'`].join(' '));
    }
    else {
      // update template inputs with the user dataset input values

      const sourceDataset = source;
      const targetDatasetState = this.getOrCreateDatasetState(dataset);

      const targetInputsById = targetDatasetState.allInputs.reduce((m, i) => m.set(i.template.id, i), new Map<string, DatasetInput>());

      for (const input of sourceDataset.inputs) {
        const target = targetInputsById.get(input.id);

        if (!target) {
          LOGGER.warn(`Could not find input '${input.id}' in dataset inputs.`);
          continue;
        }

        const source = input as any;

        if (target.value === source.value && target.label === source.label) {
          continue;
        }

        target.value = source.value;
        target.label = source.label;
      }
    }

    this.ensureFieldsLoaded(productDataset);

    if (tabIndex < this.datasetTabs.length)
      this.setDatasetTab(tabIndex, dataset.id, true);
    else
      tabIndex = this.addDatasetTab(dataset.id, false, true);
  }

  private applyLoadedDatasetFields() {
    for (const content of this.userDatasetContents) {
      const dataset = this.getDatasetByProductAndName(content.productName, content.dataset.id);
      if (!dataset)
        continue;

      const state = this.getOrCreateDatasetState(dataset);

      for (const field of content.dataset.fields) {
        const targetField = state.fieldByTemplateId.get(field.id);
        if (!targetField) {
          LOGGER.warn(`Could not find field '${field.id}' in dataset fields.`);
          continue;
        }

        targetField.exclude = field.exclude;
      }
    }

    this.updateFieldsEmitter.emit();
  }

  private applyLoadedDatasetJoins(
    tabIndex: number,
    joinType: JoinTypeOption,
    joinFields: JointDatasetFieldDto[]) {
    const tab = this.datasetTabs[tabIndex];

    tab.joinType = joinType;
    tab.joinFields = joinFields?.map(f => ({ ...f })) ?? [];
    tab.joinFieldsPristine = false;
  }

  private getFlatDatasetListFromContent(content: UserDatasetContentDto): FlatDatasetListItem[] {
    if (isNullOrUndefined(content.type) || content.type == UserDatasetContentType.Single) {
      const singleContent = <SingleUserDatasetContentDto>content;

      return [{
        productName: singleContent.productName,
        dataset: singleContent.dataset,
        joinType: JoinTypeOptions[DatasetJoinType.Inner],
        fields: []
      }];
    }
    else if (content.type == UserDatasetContentType.Joint) {
      const jointContent = <JointUserDatasetContentDto>content;

      return jointContent.items.map(i => ({
        productName: i.productName,
        dataset: i.dataset.dataset,
        joinType: JoinTypeOptions[i.dataset.joinType ?? DatasetJoinType.Inner],
        fields: i.dataset.fields?.map(f => ({ ...f })) ?? [],
      }));
    }
  }

  private createUserDatasetContentForSave(): UserDatasetContentDto {
    let content: UserDatasetContentDto;

    if (this.datasetTabs.length == 1) {
      const tab = this.datasetTabs[0];
      const productDataset = this.datasetsById.get(tab.datasetId);

      content = <SingleUserDatasetContentDto>{
        productName: productDataset.productInfo.name,
        type: UserDatasetContentType.Single,
        dataset: this.createDatasetCopyWithInputsApplied(tab.datasetId),
      };
    }
    else {
      content = <JointUserDatasetContentDto>{
        type: UserDatasetContentType.Joint,
        items: this.datasetTabs.map((t, i) => {
          const productDataset = this.datasetsById.get(t.datasetId);

          return {
            productName: productDataset.productInfo.name,
            dataset: {
              dataset: this.createDatasetCopyWithInputsApplied(t.datasetId),
              joinType: t.joinType.id,
              fields: t.joinFields?.map(f => ({ ...f })) ?? []
            }
          };
        })
      };
    }

    return content;
  }

  private createDatasetCopyWithInputsApplied(datasetId: number) {
    const datasetState = this.getOrCreateDatasetState(datasetId);

    // Create copy without inputs - those are retrieved from getCurrentInputMap
    const createdDataset = this.clonerService.deepClone({
      ...datasetState.dataset,
      inputs: []
    });

    const inputMap = this.getCurrentInputMap(datasetId);

    createdDataset.inputs = Array.from(inputMap.values());

    // The template dataset doesn't always carry the current field list, as its loaded dynamically
    // Hence, copy field list from state
    createdDataset.fields = this.clonerService.deepClone(datasetState.fields.map(sf => ({
      ...sf.field,
      // The only user changeable state is 'exclude' flag, so copy it over
      exclude: sf.exclude
    })));

    return createdDataset;
  }

  public getCurrentInputMap(datasetId?: number) {
    datasetId ??= this.selectedDatasetId$.value;

    const datasetState = this.getOrCreateDatasetState(datasetId);

    const inputs = this.clonerService.deepClone(datasetState.dataset.inputs);
    const inputMap = inputs.reduce(
      (m, i) => m.set(i.id, i),
      new Map<string, AdvancedDatasetInputDto>());

    for (const input of datasetState.allInputs) {
      const datasetInput = inputMap.get(input.template.id);

      if (!datasetInput) {
        LOGGER.warn(`Could not find input '${input.template.id}' in dataset inputs.`);
        continue;
      }

      const datasetInput_: any = datasetInput;

      datasetInput_.value = input.value;
      datasetInput_.label = input.label;
    }

    return inputMap;
  }
  //#endregion

  //#region Global Filter
  globalFilterTypeOptions$ = of(DefaultGlobalFilterTypeOptions);

  globalFilter$ = new BehaviorSubject<GlobalFilter>(DefaultGlobalFilter);
  globalFilterChange(e: GlobalFilterChangedArgs) {
    this.applyGlobalFilter(e.globalFilter);

    this.globalFilter$.next(e.globalFilter);
  }

  searchPatternForDatasets: RegExp = null;
  searchPatternForInputs: RegExp = null;
  searchPatternForFields: RegExp = null;

  showLimitToJoinableDatasets = false;
  disableLimitToJoinableDatasetsToggle = false;

  private applyGlobalFilter(globalFilter?: GlobalFilter) {
    globalFilter ??= this.globalFilter$.value;

    if (globalFilter.limitToJoinableDatasets)
      this.firstTimeAddingJoin = false;

    this.searchPatternForDatasets = null;
    this.searchPatternForInputs = null;
    this.searchPatternForFields = null;

    const all = globalFilter.type == GlobalFilterType.All;
    const globalFilterForDatasets = all || globalFilter.type == GlobalFilterType.DatasetName;
    const globalFilterForInputs = all || globalFilter.type == GlobalFilterType.InputParameter;
    const globalFilterForFields = all || globalFilter.type == GlobalFilterType.FieldName;

    const patternStr = globalFilter.searchText?.trim();
    let pattern: RegExp = null;

    if (isNullOrUndefined(patternStr) || patternStr == '') {
      pattern = null;
    }
    else {
      pattern = createSearchPattern(patternStr, 'i');
    }

    if (globalFilterForDatasets) {
      this.searchPatternForDatasets = pattern;
    }

    if (globalFilterForInputs) {
      this.searchPatternForInputs = pattern;
    }

    if (globalFilterForFields) {
      this.searchPatternForFields = pattern;
    }

    this.applyPatternForDatasets(globalFilter);
    this.controlGroups$.next(this.applyPatternToInputs(this.controlGroups$.value));
    this.filterGroups$.next(this.applyPatternToInputs(this.filterGroups$.value));
    this.fields$.next(this.applyPatternToFields(this.fields$.value));
  }

  private applyPatternForDatasets(globalFilter: GlobalFilter) {
    const currentDatasetId = this.datasetTabs[this.selectedDatasetTabIndex].datasetId;
    let possibleJoins: Set<number> = null;
    let checkPossibleJoins = false;

    if (globalFilter.limitToJoinableDatasets && this.selectedDatasetTabIndex > 0) {
      const leftDatasetId = this.datasetTabs[this.selectedDatasetTabIndex - 1].datasetId;
      possibleJoins = this.getPossibleJoins(leftDatasetId);
      checkPossibleJoins = true;
    }

    this.products$.next(this.products$.value.map(p => {
      const datasetGroups = p.datasetGroups.map(g => {
        const datasets = this.applyHidden(g.datasets, d => {
          if (checkPossibleJoins) {
            if (!possibleJoins || !possibleJoins.has(d.id)) {
              return false;
            }
          }

          if (!this.searchPatternForDatasets && !this.searchPatternForInputs && !this.searchPatternForFields)
            return true;

          if (this.searchPatternForDatasets) {
            if (this.searchPatternForDatasets.test(d.template.details.name))
              return true;
          }

          const datasetState = this.getOrCreateDatasetState(d);

          if (this.searchPatternForInputs) {
            const inputGroups = [...datasetState.controlGroups, ...datasetState.filterGroups];

            const input = inputGroups.find(g => g.inputs.find(i => !i.template.details.hidden && !i.hidden && this.searchPatternForInputs.test(i.template.details.name)));

            if (!isNullOrUndefined(input))
              return true;
          }

          if (this.searchPatternForFields) {
            const field = datasetState.fields.find(f => !f.field.hidden && this.searchPatternForFields.test(f.field.title));

            if (!isNullOrUndefined(field))
              return true;
          }

          return false;
        });

        return {
          ...g,
          datasets,
          visibleDatasets: datasets.filter(d => !d.hidden).length,
          hidden: !datasets.find(d => !d.hidden)
        };
      });

      return {
        ...p,
        datasetGroups,
        visibleDatasets: datasetGroups.reduce((n, g) => n + g.datasets.filter(d => !d.hidden).length, 0),
        hidden: !datasetGroups.find(g => !g.hidden)
      };
    }));
  }

  private applyPatternToInputs(inputGroups: DatasetInputGroup[]) {
    return inputGroups.map(g => {
      const inputs = this.applyHidden(g.inputs, i => !i.template.details.hidden && (!this.searchPatternForInputs || this.searchPatternForInputs.test(i.template.details.name)));

      return {
        ...g,
        inputs,
        hidden: !inputs.find(i => !i.hidden)
      };
    });
  }

  private applyPatternToFields(fields: DatasetField[]) {
    return this.applyHidden(fields,
      f => !f.field.hidden && (!this.searchPatternForFields || this.searchPatternForFields.test(f.field.title)));
  }

  private applyHidden<T extends { hidden?: boolean; }>(items: T[], isVisible: (item: T) => boolean) {
    return items.map(f => {
      f.hidden = !isVisible(f);
      return f;
    });
  }
  //#endregion

  //#region Products
  products$ = new BehaviorSubject<Product[]>([]);
  loadingProducts$ = new BehaviorSubject<boolean>(false);

  private loadProductDatasetTemplates() {
    this.loadingProducts$.next(true);

    let groupId = 0;
    let datasetId = 0;

    return combineLatest(this.store.select(AppSelectors.appProducts)).pipe(
      take(1),
      map(([{ list }]) => list.map(p =>
        this.customDatasetService.listDatasetTemplates(p.name).pipe(
          map(datasets => {
            const datasetGroups = createDatasetGroups();

            const datasetCount = datasetGroups.reduce((n, g) => n + g.datasets.length, 0);

            const productIcon = getIconForKnownProduct(p.name);

            const product: Product = {
              id: p.id,
              name: p.name,
              iconUrl: productIcon?.url,
              iconWidth: Math.ceil(productIcon?.widthRatio ?? 0),
              title: getTitleForKnownProduct(p.name),
              datasetGroups: datasetGroups,
              totalDatasets: datasetCount,
              visibleDatasets: datasetCount,
              hidden: false,
            };

            return product;

            function createDatasetGroups() {
              const datasetGroups = group(datasets, d => d.details.group, 'Other', sortDataset)
                .map(g => (<ProductDatasetGroup>{
                  id: ++groupId,
                  title: g.key,
                  datasets: g.items.map(d => ({
                    id: ++datasetId,
                    productInfo: p,
                    hidden: false,
                    template: d
                  })),
                  visibleDatasets: g.items.length,
                  hidden: false
                }));

              datasetGroups.sort((a, b) => {
                if (a.datasets.length) {
                  if (b.datasets.length) {
                    return a.datasets[0].template.details.groupOrder - b.datasets[0].template.details.groupOrder;
                  }
                }

                return a.title.localeCompare(b.title);
              });

              return datasetGroups;

              function sortDataset(a: AdvancedDatasetDto, b: AdvancedDatasetDto) {
                let diff = a.details.groupOrder - b.details.groupOrder;

                if (diff == 0)
                  diff = a.details.name.localeCompare(b.details.name);

                return diff;
              }
            }
          })
        )
      )),
      map(products => combineLatest(products)),
      concatMap(x => x),
      tap(products => {
        const filtererdProduct = products.filter(p => p.totalDatasets > 0);

        filtererdProduct.sort((a, b) => productOrderSort(a.name, b.name));

        this.products$.next(filtererdProduct);
        this.onProductsSet();
      }),
      catchAppError(err => {
        return of(err);
      }),
      tap(() => {
        this.loadingProducts$.next(false);
      })
    );
  }

  private onProductsSet(filter = false) {
    this.updateDatasetsByProductAndName();
    this.updateDatasetsById();

    // INFO: The 2nd condition allows swapping the dataset when filters change
    if (!this.selectedDatasetId$.value/* || this.datasetsById.get(this.selectedDatasetId$.value)?.hidden*/) {
      this.selectFirstDataset();
    }
  }
  //#endregion

  //#region Dataset
  selectedDatasetId$ = new BehaviorSubject<number>(null);
  setSelectedDatasetId(datasetId: number) {
    this.selectedDatasetId$.next(datasetId);
  }

  datasetState: DatasetState;

  product$ = new BehaviorSubject<string>(null);
  dataset$ = new BehaviorSubject<AdvancedDatasetDto>(null);

  controlGroups$ = new BehaviorSubject<DatasetInputGroup[]>([]);
  filterGroups$ = new BehaviorSubject<DatasetInputGroup[]>([]);
  fieldsLoaded$ = new BehaviorSubject<boolean>(false);
  fields$ = new BehaviorSubject<DatasetField[]>([]);

  private initDataset() {
    this.selectedDatasetId$.subscribe(() => this.onSelectedDatasetIdChanged());
  }

  datasetStateMap = new Map<number, DatasetState>();
  datasetStates: DatasetState[] = [];

  spendBillingCenter: string = null;

  private getOrCreateDatasetState(datasetOrId: ProductDataset | number) {
    const dataset = typeof datasetOrId === 'number' ? this.datasetsById.get(datasetOrId) : datasetOrId;
    let state = this.datasetStateMap.get(dataset.id);

    if (!state) {
      state = <DatasetState>{};

      const datasetTemplate = dataset.template;

      let inputId = 0;
      const inputs = datasetTemplate.inputs.map(i => {
        return <DatasetInput>{
          id: ++inputId,
          template: i,
          // we know there's a value property, but the way the types are generated does not include it
          value: (i as any).value,
          // label only applicable to dropdowns
          label: (i as any).label,
          hidden: false,
          defaultValue: i.details.defaultValue
        };
      });

      const controls = inputs.filter(i => i.template.details.kind == AdvancedDatasetInputKind.Control);
      const filters = inputs.filter(i => i.template.details.kind == AdvancedDatasetInputKind.Filter);

      let groupId = 0;
      const fieldId = 0;

      state.id = dataset.id;
      state.dataset = dataset.template;

      state.allInputs = inputs;
      state.inputMap = inputs.reduce((m, i) => m.set(i.id, i), new Map<number, DatasetInput>());

      state.controlGroups =
        group(controls, c => c.template.details.group, undefined, sortInput)
          .map(g => mapToDatasetInputGroup(g));

      state.filterGroups =
        group(filters, f => f.template.details.group, 'Other', sortInput)
          .map(g => mapToDatasetInputGroup(g));

      state.fields = this.createFields(datasetTemplate.fields, datasetTemplate.fields);
      state.fieldByTemplateId = state.fields.reduce((m, f) => m.set(f.field.id, f), new Map<string, DatasetField>());

      this.datasetStateMap.set(dataset.id, state);
      this.datasetStates = [...this.datasetStates, state];

      state.loadFields = new Subject<void>();
      state.loadFields.pipe(
        debounceTime(400),
        switchMap(() => this.loadFields(dataset, state))
      ).subscribe();

      function mapToDatasetInputGroup(g: Grouping<string, DatasetInput>): DatasetInputGroup {
        return {
          id: ++groupId,
          title: g.key,
          inputs: g.items,
          hidden: false
        };
      }

      function sortInput(a: DatasetInput, b: DatasetInput) {
        let diff = a.template.details.groupOrder - b.template.details.groupOrder;

        if (diff == 0)
          diff = a.template.details.name.localeCompare(b.template.details.name);

        return diff;
      }
    }

    return state;
  }

  private createFields(templateFields: AdvancedDatasetFieldDto[], fields: AdvancedDatasetFieldDto[]) {
    let fieldId = 0;

    const templateFieldIds = new Set<string>(templateFields.map(f => f.id));

    const stateFields = fields.filter(f => !f.hidden).map(f => (<DatasetField>{
      id: ++fieldId,
      field: f,
      icon: DefaultDatasetFieldIcon,
      hidden: false,
      exclude: f.exclude,
      // INFO: Assume UDF if not present in template fields
      // This is unfortunate, but we cannot get UDF list because SPEND returns field labels from GetResultSet, not field names
      udf: !templateFieldIds.has(f.id)
    }));

    const core = stateFields.filter(f => !f.udf);
    const udfs = stateFields.filter(f => f.udf);

    core.sort(fieldSortFunc);
    udfs.sort(fieldSortFunc);

    // UDFs always go after CORE
    return [...core, ...udfs];
  }

  private datasetsByProductAndName = new Map<string, ProductDataset>();

  private updateDatasetsByProductAndName() {
    this.datasetsByProductAndName.clear();

    for (const p of this.products$.value) {
      for (const g of p.datasetGroups) {
        for (const d of g.datasets)
          this.datasetsByProductAndName.set(this.getDatasetProductAndNameKey(p.name, d.template.id), d);
      }
    }
  }

  private getDatasetByProductAndName(productName: string, datasetName: string) {
    return this.datasetsByProductAndName.get(this.getDatasetProductAndNameKey(productName, datasetName));
  }

  private getDatasetProductAndNameKey(productName: string, datasetName: string) {
    return `${productName}/${datasetName}`;
  }

  datasetsById = new Map<number, ProductDataset>();

  private updateDatasetsById() {
    this.datasetsById.clear();

    for (const p of this.products$.value) {
      for (const g of p.datasetGroups) {
        for (const d of g.datasets)
          this.datasetsById.set(d.id, d);
      }
    }
  }

  private onSelectedDatasetIdChanged() {
    // when selected dataset changes, update all dataset properties

    let state = <DatasetState>{};
    let product: string = null;
    let dataset: AdvancedDatasetDto = null;
    let controlGroups: DatasetInputGroup[] = [];
    let filterGroups: DatasetInputGroup[] = [];
    let fieldsLoaded = false;
    let fields: DatasetField[] = [];

    const datasetId = this.selectedDatasetId$.value;
    if (datasetId) {
      const productDataset = this.datasetsById.get(datasetId);

      if (!productDataset) {
        LOGGER.error(`Could not find dataset by id '${datasetId}'`);
        return;
      }

      state = this.getOrCreateDatasetState(productDataset);

      product = productDataset.productInfo.name;
      dataset = state.dataset;
      controlGroups = state.controlGroups;
      filterGroups = state.filterGroups;
      fieldsLoaded = state.fieldsLoaded;
      fields = state.fields;

      this.ensureFieldsLoaded(productDataset, state);
    }

    controlGroups = this.applyPatternToInputs(controlGroups);
    filterGroups = this.applyPatternToInputs(filterGroups);
    fields = fieldsLoaded ? this.applyPatternToFields(fields) : [];

    this.datasetState = state;

    this.spendBillingCenter = this.currentSpendBillingCenter(this.datasetState);

    this.product$.next(product);
    this.dataset$.next(dataset);

    this.controlGroups$.next(controlGroups);
    this.filterGroups$.next(filterGroups);
    this.fieldsLoaded$.next(fieldsLoaded);
    this.fields$.next(fields);

    this.updateSelectedTabOnDatasetChange();
  }

  private selectFirstDataset() {
    if (this.products$.value) {
      const firstDatasetId = this.findSelectableDatasetId();

      this.setSelectedDatasetId(firstDatasetId);
    }
  }

  private findSelectableDatasetId() {
    const datasetId =
      this.products$.value.find(p => !p.hidden && p.datasetGroups.length)
        ?.datasetGroups.find(g => !g.hidden && g.datasets.length)
        ?.datasets.find(d => !d.hidden && !d.used)?.id;

    return datasetId;
  }

  private fieldsLoadRequests = 0;
  loadingFields = false;

  private ensureFieldsLoaded(productDataset: ProductDataset, state?: DatasetState) {
    state ??= this.getOrCreateDatasetState(productDataset);

    if (!state.fieldsLoading && !state.fieldsLoaded) {
      this.loadFieldsDebounced(productDataset, state);
    }
  }

  private loadFieldsDebounced(productDataset: ProductDataset, state?: DatasetState) {
    state ??= this.getOrCreateDatasetState(productDataset);
    state.loadFields.next();
  }

  private loadFields(productDataset: ProductDataset, state?: DatasetState) {
    state ??= this.getOrCreateDatasetState(productDataset);

    state.fieldsLoaded = false;
    state.noCorpStructure = false;
    this.fieldsLoaded$.next(false);

    if (!state.fieldsLoading) {
      state.fieldsLoading = true;
      this.fieldsLoadRequests++;
    }

    this.loadingFields = true;
    const datasetWithInputs = this.createDatasetCopyWithInputsApplied(state.id);

    return this.customDatasetService.getDatasetTemplateFields(productDataset.productInfo.name, datasetWithInputs).pipe(
      tap(fields => {
        state.fields = this.createFields(productDataset.template.fields, fields);
        state.fieldByTemplateId = state.fields.reduce((m, f) => m.set(f.field.id, f), new Map<string, DatasetField>());
      }),
      catchAppError(err => {
        LOGGER.error(`Could not load field list for ${productDataset.productInfo.name}/${productDataset.template.id}.`, err);
      }, { showErrorSnack: false }),
      mergeMap(error => {
        if (error instanceof AppError) {
          return error.getErrorContentAsJson().pipe(map((errorDto: ErrorDto) => errorDto));
        }
        return of(error);
      }),
      tap((e) => {
        if ('error' in e) {
          LOGGER.error("Field Loading Error:" + e.error);
          if (e.error == "No Corporate Structure to return.")
            state.noCorpStructure = true;
        }

        state.fieldsLoading = false;
        state.fieldsLoaded = true;
        const selectedLoaded = productDataset.id == this.selectedDatasetId$.value;

        if (selectedLoaded) {
          this.fields$.next(state.fields);
          this.fieldsLoaded$.next(true);
        }

        if (this.userDatasetContents.find(x => this.getDatasetByProductAndName(x.productName, x.dataset.id) == productDataset)) {
          this.applyLoadedDatasetFields();
        }

        if (selectedLoaded) {
          // If we are filtring on fields, want to reapply it now because list of fields may have changed
          if (this.globalFilter$.value?.type == GlobalFilterType.FieldName)
            this.applyGlobalFilter();

          this.ref.markForCheck();
        }
      }),
      finalize(() => {
        this.fieldsLoadRequests--;
        this.loadingFields = this.fieldsLoadRequests > 0;
      })
    );
  }

  private disposeFieldsLoadRequests() {
    this.fieldsLoadRequests = 0;
    this.loadingFields = false;
  }
  //#endregion

  //#region Dataset Tabs
  datasetTabs: DatasetTab[] = [
    {
      title: null,
      datasetId: null,
      joinType: JoinTypeOptions[DatasetJoinType.Inner],
      joinFields: []
    }
  ];

  selectedDatasetTabIndex = 0;

  maxDatasetTabs = 7;

  setSelectedDatasetTab(index: number) {
    this.selectedDatasetTabIndex = index;

    if (index >= 0 && index < this.datasetTabs.length) {
      const tab = this.datasetTabs[this.selectedDatasetTabIndex];

      this.setSelectedDatasetId(tab.datasetId);
    }
    else {
      this.selectedDatasetTabIndex = 0;

      this.setSelectedDatasetId(null);
    }

    this.validateLimitToJoinableDatasets();

    if (this.globalFilter$.value.limitToJoinableDatasets)
      this.applyGlobalFilter();
  }

  private validateLimitToJoinableDatasets() {
    this.showLimitToJoinableDatasets = (this.datasetTabs.length > 1);
    this.disableLimitToJoinableDatasetsToggle = this.selectedDatasetTabIndex == 0;

    if (this.showLimitToJoinableDatasets && this.firstTimeAddingJoin) {
      this.globalFilterChange({
        globalFilter: {
          ...this.globalFilter$.value,
          limitToJoinableDatasets: true
        }
      });
    }
  }

  updateSelectedTabOnDatasetChange() {
    const tab = this.datasetTabs[this.selectedDatasetTabIndex];

    const datasetId = this.selectedDatasetId$.value ?? 0;
    if (datasetId == tab.datasetId)
      return;

    if (datasetId) {
      const associatedTabIndex = this.datasetTabs.findIndex(t => t.datasetId == datasetId);
      if (associatedTabIndex != -1) {
        this.setSelectedDatasetTab(associatedTabIndex);
        return;
      }
    }

    this.updateDatasetTab(tab, this.selectedDatasetTabIndex, datasetId);
  }

  removeDatasetTab(index: number) {
    const tab = this.datasetTabs.splice(index, 1)[0];

    if (tab.datasetId)
      this.setDatasetTabAssociation(tab.datasetId, null);

    this.resetDatasetTabAssociations();

    if (index == this.selectedDatasetTabIndex) {
      if (index == this.datasetTabs.length) {
        index = this.datasetTabs.length - 1;
      }

      this.setSelectedDatasetTab(index);
    }
    else if (this.selectedDatasetTabIndex > index) {
      this.setSelectedDatasetTab(this.selectedDatasetTabIndex - 1);
    }

    this.validateLimitToJoinableDatasets();
  }

  addDatasetTab(datasetId = 0, selectTab = true, loading = false) {
    const index = this.datasetTabs.length;

    const tab: DatasetTab = {
      title: null,
      datasetId: null,
      joinType: JoinTypeOptions[DatasetJoinType.Inner],
      joinFields: []
    };

    this.updateDatasetTab(tab, index, datasetId, loading);

    this.datasetTabs.push(tab);

    if (selectTab)
      this.setSelectedDatasetTab(index);

    this.validateLimitToJoinableDatasets();

    return index;
  }

  setDatasetTab(tabIndex: number, datasetId: number, loading: boolean) {
    const tab = this.datasetTabs[this.selectedDatasetTabIndex];

    if (tab.datasetId === datasetId)
      return;

    this.updateDatasetTab(tab, tabIndex, datasetId, loading);
  }

  private updateDatasetTab(tab: DatasetTab, tabIndex: number, datasetId: number, loading = false) {
    if (tab.datasetId)
      this.setDatasetTabAssociation(tab.datasetId, null);

    tab.datasetId = datasetId;

    if (datasetId) {
      const ds = this.datasetsById.get(datasetId);

      tab.datasetName = ds.template.details.name;
      tab.datasetCategory = ds.template.details.group ?? 'Uncategorized';
    }
    else {
      tab.datasetName = null;
      tab.datasetCategory = null;
    }

    tab.joinFields = [];
    tab.joinFieldsPristine = true;

    if (tabIndex > 0) {
      const leftTab = this.datasetTabs[tabIndex - 1];

      leftTab.joinFields = [];
      leftTab.joinFieldsPristine = true;
    }

    if (datasetId) {
      const leftTab = this.datasetTabs[tabIndex - 1];
      if (leftTab) {
        leftTab.joinFields = this.getDefaultJoinFields(leftTab.datasetId, tab.datasetId);
      }

      const rightTab = this.datasetTabs[tabIndex + 1];
      if (rightTab) {
        tab.joinFields = this.getDefaultJoinFields(tab.datasetId, rightTab.datasetId);
      }
    }

    if (datasetId)
      this.setDatasetTabAssociation(datasetId, tabIndex);

    this.validateDatasetTab(tab);
  }

  validationQueued = false;


  onChangeToActiveDataset() {
    const tab = this.datasetTabs[this.selectedDatasetTabIndex];
    if (!tab)
      return;

    this.validateDatasetTab(tab);

    const productDataset = this.datasetsById.get(tab.datasetId);

    // in-4681: ExportCS depends on inputs
    // in-5409: Location/Users depends on inputs
    if (inputDependentDatasets.includes(productDataset.template.id)) {
        this.loadFieldsDebounced(productDataset);
    }
  }

  validateDatasetTab(tab: DatasetTab) {
    if (this.validationQueued)
      return;

    this.validationQueued = true;


    setTimeout(() => {
      if (this._destroyed)
        return;

      this.validationQueued = false;

      let valid = true;

      if (tab.datasetId) {
        if (!this.pristine) {
          const invalidRequiredInputs = this.getInvalidRequiredInputs(tab.datasetId);
          if (invalidRequiredInputs.length) {
            valid = false;
            this.markInvalidRequiredInputs(invalidRequiredInputs);
          }
        }
      }
      tab.invalid = !valid;
    });

  }

  clearDatasetTabInvalid() {
    for (const tab of this.datasetTabs)
      tab.invalid = false;
  }

  canAddDatasetTabs() {
    return this.datasetTabs.length < this.maxDatasetTabs;
  }

  private setDatasetTabAssociation(datasetId: number, tabIndex?: number) {
    const dataset = this.datasetsById.get(datasetId);
    const wasUsed = dataset.used;

    dataset.used = !isNullOrUndefined(tabIndex);
    dataset.tabIndex = tabIndex;
  }

  private resetDatasetTabAssociations() {
    for (let i = 0; i < this.datasetTabs.length; ++i) {
      const tab = this.datasetTabs[i];
      if (tab.datasetId) {
        this.setDatasetTabAssociation(tab.datasetId, i);
      }
    }
  }
  //#endregion

  //#region Join Dialog
  @ViewChild('joinDialogTemplate') joinDialogTemplate: TemplateRef<any>;

  joinDialogRef: MatDialogRef<any>;

  joinDialog: JoinDialogModel = {
    datasets: [],
    selectedIndex: -1
  };

  joinTypes = JoinTypeOptions;

  joinLeftDataset: JoinDialogDataset;
  joinRightDataset: JoinDialogDataset;

  joinFields: JointDatasetFieldDto[];

  leftFieldSearchCtrl: FormControl;
  rightFieldSearchCtrl: FormControl;

  joinLeftDatasetFields: JoinDialogDatasetField[];
  joinRightDatasetFields: JoinDialogDatasetField[];

  defaultJoinFields: JointDatasetFieldDto[] = [];
  joinCanResetToDefaults = false;
  joinFieldsPristine = true;

  @ViewChildren('jointDatasetsContainer') jointDatasetsContainer: QueryList<ElementRef<HTMLDivElement>>;

  jointDatasetsContainerScroll = 0;

  updateJointDatasetsContainerOffset() {
    const scroll = this.getJointDatasetContainerScrollForTab(this.joinDialog.selectedIndex);

    this.setJointDatasetsContainerScroll(scroll);
  }

  private getJointDatasetContainerScrollLimits() {
    let max = this.getJointDatasetContainerScrollForTab(0);
    let min = this.getJointDatasetContainerScrollForTab(this.datasetTabs.length - 2);

    if (this.datasetTabs.length > 2) {
      max += 100;
      min -= 100;
    }

    return { min, max };
  }

  private getJointDatasetContainerScrollForTab(tabIndex: number) {
    const container = this.jointDatasetsContainer?.first?.nativeElement;
    if (!container)
      return 0;

    const parent = container.parentElement;
    const parentWidth = parent.offsetWidth;

    const scroll = (parentWidth / 2) - this.getOffsetToJointCenter(tabIndex + 1);

    return scroll;
  }

  jointDatasetContainerDragging(e: DraggableDeltaEvent) {
    this.setJointDatasetsContainerScroll(this.jointDatasetsContainerScroll + e.dx, true);
  }

  private setJointDatasetsContainerScroll(value: number, snap = false) {
    const limits = this.getJointDatasetContainerScrollLimits();

    value =
      ((value < limits.min)
        ? limits.min
        : ((value > limits.max) ? limits.max : value));

    this.jointDatasetsContainerScroll = value;

    const container = this.jointDatasetsContainer?.first?.nativeElement;
    if (container) {
      if (snap) {
        container.style.transition = `none`;
        container.offsetLeft;
      }
      else {
        container.style.transition = `transform 0.2s`;
      }

      container.style.transform = `translateX(${value}px)`;
    }
  }

  private getOffsetToJointCenter(i: number) {
    return (Math.max(i - 1, 0) * (190 + 64)) + 190 + 130 / 2;
  }

  openJoinDialog(tabIndex: number) {
    if (!this.datasetTabs[tabIndex]?.datasetId || !this.datasetTabs[tabIndex + 1]?.datasetId) {
      return;
    }

    this.joinDialog.datasets = this.datasetTabs.map((t, i) => {
      const dataset = this.datasetsById.get(t.datasetId);
      const template = dataset?.template;

      let fields = template?.fields.map(f => <JoinDialogDatasetField>{
        id: f.id,
        title: f.title
      }) ?? [];

      fields = distinctBy(fields, f => f.id);
      fields.sort((a, b) => a.title.localeCompare(b.title));

      return <JoinDialogDataset>{
        id: dataset.id,
        invalid: !!dataset,
        selected: false,
        title: template?.details.name,
        product: dataset?.productInfo.name,
        fields: fields,
        joinType: t.joinType,
        joinFields: t.joinFields?.map(f => ({ ...f })) ?? []
      };
    });

    if (!this.leftFieldSearchCtrl) {
      this.leftFieldSearchCtrl = new FormControl('');
      this.rightFieldSearchCtrl = new FormControl('');

      this.subscribeToSearchCtrl(this.leftFieldSearchCtrl, f => {
        f = f.trim();
        if (f) {
          const filterRegex = createSearchPattern(f);

          this.joinLeftDatasetFields =
            this.joinLeftDataset.fields.filter(f => filterRegex.test(f.title));
        }
        else {
          this.joinLeftDatasetFields = this.joinLeftDataset.fields;
        }
      });

      this.subscribeToSearchCtrl(this.rightFieldSearchCtrl, f => {
        f = f.trim();
        if (f) {
          const filterRegex = createSearchPattern(f);

          this.joinRightDatasetFields =
            this.joinRightDataset.fields.filter(f => filterRegex.test(f.title));
        }
        else {
          this.joinRightDatasetFields = this.joinRightDataset.fields;
        }
      });
    }

    this.setJoinDataset(tabIndex);

    this.joinDialogRef = this.dialog.open(this.joinDialogTemplate, {
      width: '1050px',
      height: '650px',
      panelClass: 'dialog-no-padding'
    });

    this.joinDialogRef.afterClosed().subscribe(() => {
      for (let i = 0; i < this.joinDialog.datasets.length; ++i) {
        const joinDataset = this.joinDialog.datasets[i];
        const tab = this.datasetTabs[i];

        tab.joinType = joinDataset.joinType;
        tab.joinFields = joinDataset.joinFields?.map(f => ({ ...f })) ?? [];
      }
    });
  }

  setJoinDataset(index: number) {
    this.datasetTabs[index].joinFieldsPristine = false;

    this.joinDialog.selectedIndex = index;

    for (let i = 0; i < this.joinDialog.datasets.length; ++i) {
      const dataset = this.joinDialog.datasets[i];

      dataset.selected = i == index || i == (index + 1);
    }

    this.joinLeftDataset = this.joinDialog.datasets[index];
    this.joinRightDataset = this.joinDialog.datasets[index + 1];

    if (!this.joinLeftDataset.joinFields.length)
      this.joinAddField();

    this.joinLeftDatasetFields = this.joinLeftDataset.fields;
    this.joinRightDatasetFields = this.joinRightDataset.fields;

    this.leftFieldSearchCtrl.reset('');
    this.rightFieldSearchCtrl.reset('');

    this.defaultJoinFields = this.getDefaultJoinFields();
    this.joinCanResetToDefaults = this.defaultJoinFields.length > 0;

    this.updateJointDatasetsContainerOffset();
  }

  private subscribeToSearchCtrl(ctrl: FormControl, callback: (filter: string) => void) {
    ctrl.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      tap(s => callback(s))
    ).subscribe();
  }

  closeJoinDialog() {
    this.joinDialogRef.close();
  }

  joinAddField(field?: JointDatasetFieldDto) {
    this.joinFieldsPristine = false;

    this.joinLeftDataset.joinFields.push({
      leftFieldId: null,
      rightFieldId: null,
      ...field
    });
  }

  joinRevertFieldsToDefault() {
    this.joinLeftDataset.joinFields.length = 0;

    for (const defaultField of this.defaultJoinFields) {
      this.joinAddField(defaultField);
    }

    this.joinFieldsPristine = true;
  }

  hasValidJoinFields() {
    return !!this.joinLeftDataset.joinFields.find(f => f.leftFieldId && f.rightFieldId);
  }

  joinRemoveField(index: number) {
    this.joinFieldsPristine = false;

    this.joinLeftDataset.joinFields.splice(index, 1);

    if (this.joinLeftDataset.joinFields.length == 0) {
      this.joinAddField();
    }
  }

  joinFieldChange(index: number) {
    this.joinFieldsPristine = false;
  }

  private datasetDefaultJoinFieldsLookup: DatasetDefaultJoinFieldsLookupDto;
  private defaultJoinFieldsCache = new Map<string, JointDatasetFieldDto[]>();
  private possibleJoinsCache = new Map<number, Set<number>>();
  loadingDefaultJoinFields$ = new BehaviorSubject<boolean>(false);

  private loadDefaultJoinFields() {
    this.loadingDefaultJoinFields$.next(true);

    this.defaultJoinFieldsCache.clear();

    this.customDatasetService.getDefaultJoinFields().pipe(
      tap(lookup => {
        this.datasetDefaultJoinFieldsLookup = lookup;

        this.populatePossibleJoinsCache();

        for (const item of lookup.items) {
          this.defaultJoinFieldsCache.set(
            this.makeDefautJoinFieldsKey(item.key),
            item.fields);
        }
      }),
      catchAppError(err => {
        return of(err);
      })
    ).subscribe(() => {
      this.loadingDefaultJoinFields$.next(false);
    });
  }

  private populatePossibleJoinsCache() {
    this.possibleJoinsCache.clear();

    for (const item of this.datasetDefaultJoinFieldsLookup.items) {
      const leftPoductDataset = this.getDatasetByProductAndName(item.key.left.productName, item.key.left.datasetId);
      const rightProductDataset = this.getDatasetByProductAndName(item.key.right.productName, item.key.right.datasetId);

      if (!leftPoductDataset) {
        continue;
      }

      if (!rightProductDataset) {
        continue;
      }

      this.addPossibleJoin(leftPoductDataset.id, rightProductDataset.id);
      this.addPossibleJoin(rightProductDataset.id, leftPoductDataset.id);
    }
  }

  private addPossibleJoin(datasetId: number, joinedDatasetId: number) {
    let possibleJoinsSet = this.getPossibleJoins(datasetId);
    if (!possibleJoinsSet) {
      possibleJoinsSet = new Set<number>();

      this.possibleJoinsCache.set(datasetId, possibleJoinsSet);
    }

    possibleJoinsSet.add(joinedDatasetId);
  }

  private getPossibleJoins(datasetId: number) {
    return this.possibleJoinsCache.get(datasetId);
  }

  private getDefaultJoinFields(leftId?: number, rightId?: number) {
    leftId ??= this.joinLeftDataset.id;
    rightId ??= this.joinRightDataset.id;

    if (!leftId || !rightId)
      return [];

    const key = this.makeDefautJoinFieldsKeyFromIds(leftId, rightId);

    let fields = this.defaultJoinFieldsCache.get(key);
    if (!fields) {
      const key = this.makeDefautJoinFieldsKeyFromIds(rightId, leftId);

      fields = this.defaultJoinFieldsCache.get(key);
      if (!fields)
        return [];

      // As this is reverse lookup, reverse the field names
      fields = fields.map(f => ({
        leftFieldId: f.rightFieldId,
        rightFieldId: f.leftFieldId,
      }));
    }

    return fields.map(f => ({ ...f }));
  }

  private makeDefautJoinFieldsKeyFromIds(leftId: number, rightId: number) {
    const left = this.datasetsById.get(leftId);
    const right = this.datasetsById.get(rightId);

    return this.makeDefautJoinFieldsKey({
      left: {
        productName: left.productInfo.name,
        datasetId: left.template.id
      },
      right: {
        productName: right.productInfo.name,
        datasetId: right.template.id
      }
    });
  }

  private makeDefautJoinFieldsKey(key: DatasetTemplateReferencePairDto) {
    return `${key.left.productName}/${key.left.datasetId},${key.right.productName}/${key.right.datasetId}`;
  }
  //#endregion

  // whether required fields should be highlighted
  inputChange(e: DatasetInputChangeEventArgs) {
    this.updateInput(e.kind, e.inputId, e.changes);
  }

  private currentSpendBillingCenter(datasetState: DatasetState):string {
    const input = datasetState?.allInputs?.find(i => i.template.id == "CountryID");

    return input?.value?.value;
  }

  private updateInput(kind: AdvancedDatasetInputKind, id: number, updates: any) {
    const input = this.datasetState.inputMap.get(id);
    if (!input)
      return;

    for (const k of Object.keys(updates))
      (input as any)[k] = updates[k];

    this.spendBillingCenter = this.currentSpendBillingCenter(this.datasetState);

    this.propertiesDirty = true;
    this.pristine = false;

    this.onChangeToActiveDataset();
  }

  private getInvalidRequiredInputs(datasetId: number = null) {
    return Array.from(this.getInvalidRequiredInputsGenerator(datasetId));
  }

  private * getInvalidRequiredInputsGenerator(datasetId: number = null) {
    let tabs = this.datasetTabs;

    if (!isNullOrUndefined(datasetId))
      tabs = tabs.filter(t => t.datasetId === datasetId);

    for (const datasetTab of tabs) {
      if (!datasetTab.datasetId)
        continue;

      const datasetState = this.getOrCreateDatasetState(datasetTab.datasetId);

      const requiredGroups = group(datasetState.allInputs, i => i.template.details.requiredGroup);

      for (const { key, items } of requiredGroups) {
        if (isNullOrWhitespace(key))
          continue;

        if (!this.isValidRequiredGroup(items)) {
          yield {
            input: items[0],
            datasetId: datasetTab.datasetId,
            error: null
          };
        }
      }

      for (const i of datasetState.allInputs) {
        if (i.template.details.requiredGroup) {
          continue;
        }
        if (!i.template.details.hidden) {
          const isDateRange = (i.template.details.name === 'Date Range') && (i.value?.operator === AdvancedDatasetRelativeDatesOperator.Range);
          // Once a relative dates input has been fully or partially filled its subject to validation to prevent incomplete data
          const validateRelativeDates = (this.isRelativeDatesInput(i) && this.isRelativeDatesInputPopulated(i))

          if (i.template.details.required || isDateRange || validateRelativeDates){
            const result = this.isInputValueSet(i);
            if (!result.valid) {
              yield {
                input: i,
                datasetId: datasetTab.datasetId,
                error: result.error
              };
            }
          }
        }
      }
    }
  }
  private isValidRequiredGroup(inputs: DatasetInput[],) {
    // Currently requiredGroups can only be check boxes. so this only checks if ONE of the check boxes is selected therefor making the RequiredGroup Valid in this context
    return !!inputs.find(i => i.value);
  }

  private isRelativeDatesInputPopulated(input: DatasetInput): boolean {
    const value = <AdvancedDatasetRelativeDatesInputValueDto>input.value;

    if (value === null || typeof value !== 'object') {
      return false;
    }

    switch (value.operator) {
      case AdvancedDatasetRelativeDatesOperator.SpecificDates: {
        if ((value.startDate || value.endDate))
          return true;
      } break;

      case AdvancedDatasetRelativeDatesOperator.InLast:
      case AdvancedDatasetRelativeDatesOperator.InNext: {
        if (value.unitsCount > 0)
          return true;
      } break;

      case AdvancedDatasetRelativeDatesOperator.Range: {
        if (value.unitsCount > 0 || value.units2Count > 0)
          return true;
      } break;
    }

    return false;
  }

  private isRelativeDatesInput(input: DatasetInput) {
    return (input.template.type == AdvancedDatasetInputType.RelativeDates
      || input.template.type == AdvancedDatasetInputType.RelativeYearPeriod)
  }

  private isInputValueSet(input: DatasetInput): { valid: boolean, error: string; } {
    let defaultValue = input.template.details.defaultValue;
    const alwaysIncluded = input.template.details.alwaysIncluded;

    if (input.template.type == AdvancedDatasetInputType.Dropdown) {
      const dropdownTemplate = <AdvancedDatasetDropdownInputDto>input.template;

      if (!isNullOrUndefined(input.template.details.defaultValue)) {
        if (typeof input.template.details.defaultValue === 'object') {
          defaultValue = input.template.details.defaultValue.Value;
        }
      }

      if (dropdownTemplate.multiple) {
        if (isNullOrUndefined(input.value) || input.value === '') {
          if (input.value === defaultValue && alwaysIncluded)
            return { valid: true, error: null };

          return { valid: false, error: null };
        }
        else {
          if (input.value === defaultValue)
            return { valid: true, error: null };
        }

        if (input.value.length == 0)
          return { valid: false, error: null };

        return { valid: true, error: null };
      }
    }

    if ((defaultValue === null || defaultValue === '') && alwaysIncluded) {
      if (input.value === defaultValue) {
        return { valid: true, error: null };
      }
    }

    if (isNullOrUndefined(input.value) || input.value === '' || !('' + input.value).trim().length)
      return { valid: false, error: null };

    if (this.isRelativeDatesInput(input)) {
      const value = <AdvancedDatasetRelativeDatesInputValueDto>input.value;

      if (typeof value !== 'object') {
        return { valid: false, error: null };
      }
      const invalidDateRangeError = 'Please select a valid date range';
      const invalidRestrictedDateRangeError = 'This dataset is restricted. Please select a date range which does not exceed 12 months';

      switch (value.operator) {
        case AdvancedDatasetRelativeDatesOperator.SpecificDates: {
          if (!(value.startDate && value.endDate))
            return { valid: false, error: invalidDateRangeError };

          if (input.template.details.maximumDateRange > 0) {
            const startMoment = moment(value.startDate);
            const endMoment = moment(value.endDate);

            const duration = moment.duration(endMoment.diff(startMoment)).add(1, 'days');

            if (duration.asMonths() > input.template.details.maximumDateRange)
              return { valid: false, error: invalidRestrictedDateRangeError };
          }
        } break;

        case AdvancedDatasetRelativeDatesOperator.InThis: {
          if (isNullOrUndefined(value.unitType))
            return { valid: false, error: input.template.details.maximumDateRange > 0 ? invalidRestrictedDateRangeError : invalidDateRangeError };

          if (input.template.details.maximumDateRange > 0) {
            const maximumUnits = maximumUnitsFromUnitType(value.unitType, input.template.details.maximumDateRange);

            if (maximumUnits <= 0)
              return { valid: false, error: invalidDateRangeError };
          }
        } break;

        case AdvancedDatasetRelativeDatesOperator.InLast:
        case AdvancedDatasetRelativeDatesOperator.InNext: {
          if (isNullOrUndefined(value.unitType) || !value.unitsCount)
            return { valid: false, error: input.template.details.maximumDateRange > 0 ? invalidRestrictedDateRangeError : invalidDateRangeError };

          if (input.template.details.maximumDateRange > 0) {
            if (value.unitsCount < 1) {
              return { valid: false, error: invalidDateRangeError };
            }

            const maximumUnits = maximumUnitsFromUnitType(value.unitType, input.template.details.maximumDateRange);

            if (maximumUnits < value.unitsCount)
              return { valid: false, error: invalidRestrictedDateRangeError };
          }
        } break;

        case AdvancedDatasetRelativeDatesOperator.Range: {
          if (!input.template.details.required) {
            if ((!value.unitsCount && value.units2Count) || (value.unitsCount && !value.units2Count)){
              return { valid: false, error: invalidDateRangeError };
            }
            return { valid: true, error: null };
          }
          if (isNullOrUndefined(value.unit2Type) || !value.units2Count
            || isNullOrUndefined(value.unitType) || !value.unitsCount) {
            return { valid: false, error: input.template.details.maximumDateRange > 0 ? invalidRestrictedDateRangeError : invalidDateRangeError };
          }

          if (input.template.details.maximumDateRange > 0) {
            const months1 = unitsAsMonths(value.unitType, value.unitsCount);
            const months2 = unitsAsMonths(value.unit2Type, value.units2Count);

            if ((months1 + months2) > input.template.details.maximumDateRange)
              return { valid: false, error: input.template.details.maximumDateRange > 0 ? invalidRestrictedDateRangeError : invalidDateRangeError };
          }
        } break;
      }
    }

    return { valid: true, error: null };
  }
}

export const inputDependentDatasets = ['ExportCS', 'GetLocEmp'];

export interface DatasetState {
  id: number;
  dataset: AdvancedDatasetDto;

  allInputs: DatasetInput[];
  inputMap: Map<number, DatasetInput>;

  controlGroups: DatasetInputGroup[];
  filterGroups: DatasetInputGroup[];
  fieldsLoaded: boolean;
  fieldsLoading: boolean;
  noCorpStructure: boolean;
  fields: DatasetField[];
  fieldByTemplateId: Map<string, DatasetField>;
  loadFields: Subject<void>;

  selected: boolean;
}

export interface DatasetEditorDialogData {
  appSettings: AppSettingsDto;
  userDatasetId: number;

  // INFO: index of joint dataset, id of field
  referencedFields?: DatasetReferencedField[];
}

export interface DatasetReferencedField {
  index: number,
  id: string,
  schemaField: DataSchemaItem,
}

export interface DatasetEditorDialogResult {
  userDataset?: ListUserDatasetDto;
  validationState?: StoredUserDatasetValidationStateDto;
  refresh?: boolean;
  reset?: boolean;
  reload?: boolean;
}

export interface DatasetChangesClassification {
  kind: DatasetChangesKind;
  datasets?: DatasetChangesItem[];
  brokenFieldReferences?: DatasetReferencedField[];
}

interface DatasetElementChange<T> {
  id?: any;
  previous?: T | null;
  current?: T | null;

  added?: boolean;
  removed?: boolean;
  changed?: boolean;
}

class DatasetElementChangeSet<T> {
  readonly all: DatasetElementChange<T>[] = [];
  readonly added = new Map<any, DatasetElementChange<T>>();
  readonly removed = new Map<any, DatasetElementChange<T>>();
  readonly changed = new Map<any, DatasetElementChange<T>>();

  get any(): boolean {
    return this.all.length > 0;
  }

  get anyAdded(): boolean {
    return this.added.size > 0;
  }

  get anyRemoved(): boolean {
    return this.removed.size > 0;
  }

  get anyChanged(): boolean {
    return this.changed.size > 0;
  }

  constructor(
    private readonly keyGetter: (item: T) => any,
    private readonly isRemoved: (item: T) => boolean,
    private readonly areSame: (a: T, b: T) => boolean
  ) { }

  public calculate(previousItems: T[], currentItems: T[]) {
    type WithId<T> = { id: any; item: T; };

    const previousList = (previousItems ?? []).map(x => ({ id: this.keyGetter(x), item: x }));
    const currentList = (currentItems ?? []).map(x => ({ id: this.keyGetter(x), item: x }));

    const previousMap = previousList.reduce((m, x) => m.set(x.id, x), new Map<any, WithId<T>>());
    const currentMap = currentList.reduce((m, x) => m.set(x.id, x), new Map<any, WithId<T>>());

    const changes: DatasetElementChange<T>[] = [];

    for (const previous of previousList) {
      const current = currentMap.get(previous.id);
      this.process(changes, current, previous);
    }

    for (const current of currentList) {
      const previous = previousMap.get(current.id);
      this.process(changes, current, previous);
    }

    this.finalize(changes);
  }

  private process(changes, current, previous) {
    const currentRemoved = !current || this.isRemoved(current.item);
    const previousRemoved = !previous || this.isRemoved(previous.item);

    if (currentRemoved && !previousRemoved) {
      changes.push({
        id: previous.id,
        previous: previous.item,
        current: current?.item,
        removed: true
      });
    }
    else if (previousRemoved && !currentRemoved) {
      changes.push({
        id: current.id,
        previous: previous?.item,
        current: current.item,
        added: true
      });
    }
    else if (!previousRemoved && !currentRemoved) {
      if (!this.areSame(previous.item, current.item)) {
        changes.push({
          id: previous.id,
          previous: previous.item,
          current: current.item,
          changed: true
        });
      }
    }
  }

  private finalize(changes: DatasetElementChange<T>[]) {
    this.all.length = 0;
    this.all.push(...changes);

    this.added.clear();
    this.removed.clear();
    this.changed.clear();

    this.all.forEach(x => {
      if (x.added)
        this.added.set(x.id, x);

      if (x.removed)
        this.removed.set(x.id, x);

      if (x.changed)
        this.changed.set(x.id, x);
    });
  }
}

interface DatasetChangesItem {
  /** List of changed fields */
  fields: DatasetElementChangeSet<AdvancedDatasetFieldDto>;

  /** List of changed inputs */
  inputs: DatasetElementChangeSet<AdvancedDatasetInputDto>;

  joinFields?: boolean;
  joinTypes?: boolean;
}

export enum DatasetChangesKind {
  /* Indicates no changes have been made. Dataset update is not necessary. */
  None,

  /** Changes made trigger a refresh. */
  Refresh,

  /** Changes altered the dataset or join but does not require the visual to reset*/
  DatasetChangeNoFieldsBroken,

  /** Changes made cause the dataset to reset.
   * It is still the same dataset, but any visual using it should be reset as well. */
  Reset,

  /** Changes have been made to referenced datasets.
   * It is a different dataset now. */
  DatasetChange
}

interface FlatDatasetListItem {
  productName: string;
  dataset: AdvancedDatasetDto;
  joinType?: JoinTypeOption;
  fields?: JointDatasetFieldDto[];
}

function fieldSortFunc(a: DatasetField, b: DatasetField) {
  let diff = a.field.order - b.field.order;

  if (diff == 0)
    diff = a.field.title.localeCompare(b.field.title);

  return diff;
}
